import React from 'react';
import styled from 'styled-components';
import { Box, HorizontalScroll, TextLink, Typography } from '@drone-js/core';
import { connect, useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { getRecentlySearch } from 'component/common/TextSearch/suggestionUtils';
import { LocalStorageKey } from 'core-framework/constant/StorageKeys';
import { removeItemFromLocalStorage } from 'core-framework/window/storage';
import { StringFormatter } from '@agoda/react-handyman';
import { RecentSearchItem } from 'core/type/SuggestionResult.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesClearAllHistoryClickEventActivitiesFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesClearAllHistoryClickEventActivitiesManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { TextSearchType } from 'core/type/TextSearch.types';
import { getCategoryName } from 'core/utils/categoryUtils';
import { mapStateToProps, mapDispatchToProps, RecentSearchProps } from 'component/common/TextSearch/TextSearchResult/RecentSearch/RecentSearch.selectors';

interface HistoryCardProps {
    items: RecentSearchItem[];
    onClick: (item: RecentSearchItem, index: number) => void;
    maxWidth: string;
}

const HorizontalScrollStyled = styled(HorizontalScroll)`
    &&&& > * {
        overflow: visible;
    }
`;

const HistoryCard: React.FC<HistoryCardProps> = ({ items, onClick, maxWidth }) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const isSearchBoxFeatureGroup = useSelector(selectFeatureBehaviorFromBootstrap).SearchBoxFeatureGroup;

    const getActivitySubTitle = React.useCallback(
        (item: RecentSearchItem) => {
            if (isSearchBoxFeatureGroup) {
                const categoryName = getCategoryName(item, cms);
                return categoryName ? StringFormatter.format(cms[320129], categoryName, item.cityName) : item.cityName;
            }
            const countryName = item.countryName ? ` • ${item.countryName}` : '';
            return StringFormatter.format(`${item.cityName}${countryName}`);
        },
        [cms, isSearchBoxFeatureGroup],
    );

    return (
        <HorizontalScrollStyled
            data-element-name="activities-search-history-horizontal-scroll"
            nextLabel={cms[130706]}
            previousLabel={cms[130765]}
            ps="4"
            role="listbox"
            aria-label={cms[310623]}
        >
            {items.map((item, index) => (
                <Box
                    as="button"
                    key={`history-${index}`}
                    onClick={() => onClick(item, index)}
                    data-element-name="activities-search-history-item"
                    data-testid="activities-search-history-item"
                    borderWidth="1"
                    borderColor="base.default"
                    borderRadius="8"
                    borderStyle="solid"
                    interactive="product.primary"
                    p="12"
                    m="4"
                    textAlign="start"
                    role="option"
                >
                    <Box width="max-content" maxWidth={maxWidth}>
                        <Typography variant="body-s-strong" lineClamp={1}>
                            {item.type === TextSearchType.City ? item.cityName : item.activityName}
                        </Typography>
                        {item.type === TextSearchType.Activity && (
                            <Typography variant="body-s" lineClamp={1} textColor="base.subtle">
                                {getActivitySubTitle(item)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </HorizontalScrollStyled>
    );
};
HistoryCard.displayName = 'HistoryCard';

const RecentSearchComponent: React.FC<RecentSearchProps> = (props: RecentSearchProps) => {
    const { cms, config, screenSize, isActivityRedirectExperimentEnabled, onHistoryItemClick } = props;
    // eslint-disable-next-line no-nested-ternary
    const historyCardWidth = screenSize.mobile ? '220px' : screenSize.tablet ? '240px' : '250px';
    const [recentCities, setRecentCities] = React.useState(getRecentlySearch(LocalStorageKey.RECENT_CITIES));
    const [recentActivities, setRecentActivities] = React.useState(getRecentlySearch(LocalStorageKey.RECENT_ACTIVITIES));
    const hasRecentSearch = recentCities.length > 0 || recentActivities.length > 0;
    if (recentCities.length === 0 && recentActivities.length === 0) return null;

    const handleClearAll = () => {
        removeItemFromLocalStorage(LocalStorageKey.RECENT_CITIES);
        setRecentCities([]);
        removeItemFromLocalStorage(LocalStorageKey.RECENT_ACTIVITIES);
        setRecentActivities([]);
        trackElementEvent<ActivitiesClearAllHistoryClickEventActivitiesFieldsV2>(ActivitiesClearAllHistoryClickEventActivitiesManifestV2.ids, {});
    };

    const onHistoryClick = (recentSearch: RecentSearchItem, index: number) => {
        onHistoryItemClick(recentSearch, index, config, isActivityRedirectExperimentEnabled);
    };

    return (
        <Box px="12" pb="8" maxWidth="744px" mx="auto" width="100%">
            {hasRecentSearch && (
                <Box display="inline-flex" flexDirection="row" width="100%" justifyContent="space-between" py="12">
                    <Typography variant="body-default-strong" ps="8">
                        {cms[310623]}
                    </Typography>
                    <TextLink hierarchy="primary" label={cms[310624]} onClick={handleClearAll} />
                </Box>
            )}
            {recentCities && recentCities.length > 0 && <HistoryCard items={recentCities} onClick={onHistoryClick} maxWidth="fit-content" />}
            {recentActivities && recentActivities.length > 0 && <HistoryCard items={recentActivities} onClick={onHistoryClick} maxWidth={historyCardWidth} />}
        </Box>
    );
};

RecentSearchComponent.displayName = 'RecentSearch';
const RecentSearch = connect(mapStateToProps, mapDispatchToProps)(RecentSearchComponent);
export default RecentSearch;
